import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from './App';
import Header from './components/Header';
import Post from './components/Post';
import PostDetails from './components/PostDetails';
import { useNavigate } from 'react-router-dom';

/**
 * Search component allows users to query Reddit with optional filters.
 * Users can specify a subreddit, sort type and time window. Results
 * are displayed using the existing Post component for consistency.
 */
function Search() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [subreddit, setSubreddit] = useState('');
  const [sort, setSort] = useState('relevance');
  const [time, setTime] = useState('all');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // State for selection and details
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [expandedPost, setExpandedPost] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const postRefs = useRef([]);

  const token = localStorage.getItem('reddit_access_token');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query || !token) return;
    setLoading(true);
    let url = `https://oauth.reddit.com/search.json?q=${encodeURIComponent(query)}&limit=35&sort=${sort}&t=${time}`;
    // Restrict to a specific subreddit if provided
    if (subreddit.trim()) {
      url += `&restrict_sr=1&subreddit=${encodeURIComponent(subreddit.trim())}`;
    }
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.data && data.data.children) {
        setResults(data.data.children);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    }
    setLoading(false);
  };

  // Fetch full post details and comments for a given post. Wrap in useCallback so
  // the function reference is stable across renders. This prevents triggering
  // dependent effects on every render and satisfies the exhaustive-deps rule.
  const selectPost = useCallback(async (postData) => {
    if (!postData) return;
    setDetailsLoading(true);
    setExpandedPost(null);
    try {
      const res = await fetch(`https://oauth.reddit.com${postData.permalink}.json`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      const fullPost = json[0].data.children[0].data;
      const comments = json[1]?.data?.children || [];
      setExpandedPost({
        ...fullPost,
        comments: comments,
      });
    } catch (err) {
      console.error('Failed to fetch post details:', err);
    }
    setDetailsLoading(false);
  }, [token]);

  // Keyboard navigation for search results.
  // We define the handler with useCallback so that its identity is stable across renders.
  // This prevents the exhaustives-deps ESLint rule from complaining about unstable functions.
  const handleKeyPress = useCallback(
    (e) => {
      // Ignore if typing in an input or select/textarea
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      // No results, nothing to navigate
      if (results.length === 0) return;
      switch (e.key) {
        case 'j':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'k':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'o':
          e.preventDefault();
          if (selectedIndex >= 0 && results[selectedIndex]) {
            // Call the stable selectPost callback to load the full post details
            selectPost(results[selectedIndex].data);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setExpandedPost(null);
          break;
        default:
          break;
      }
    },
    [results, selectedIndex, selectPost]
  );

  // Attach the keydown handler on mount and clean up on unmount.
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Scroll to the selected post when navigating with j/k
  useEffect(() => {
    if (selectedIndex >= 0 && postRefs.current[selectedIndex] && !expandedPost) {
      postRefs.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedIndex, expandedPost]);

  // Listen for file system commands on the search page
  useEffect(() => {
    const handleFileSystemCommand = (e) => {
      // Navigate to home then dispatch event to trigger feed command
      navigate('/');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('fileSystemCommand', { detail: e.detail }));
      }, 10);
    };
    window.addEventListener('fileSystemCommand', handleFileSystemCommand);
    return () => window.removeEventListener('fileSystemCommand', handleFileSystemCommand);
  }, [navigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header reused from the rest of the app. Provide placeholder username. */}
      <Header username="search" onLogout={() => {}} />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Feed and search controls */}
        <div style={{ flex: expandedPost ? '0 0 45%' : '1', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div
            style={{
              padding: '15px',
              borderTop: `1px solid ${theme.colors.border}`,
            }}
          >
            <form
              onSubmit={handleSearch}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}
            >
              <input
                type="text"
                placeholder="Search term"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{
                  flex: '1 1 200px',
                  padding: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '4px',
                  background: theme.colors.background2,
                  color: theme.colors.foreground,
                }}
              />
              <input
                type="text"
                placeholder="Subreddit (optional)"
                value={subreddit}
                onChange={(e) => setSubreddit(e.target.value)}
                style={{
                  flex: '0 0 180px',
                  padding: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '4px',
                  background: theme.colors.background2,
                  color: theme.colors.foreground,
                }}
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  flex: '0 0 120px',
                  padding: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '4px',
                  background: theme.colors.background2,
                  color: theme.colors.foreground,
                }}
              >
                <option value="relevance">Relevance</option>
                <option value="hot">Hot</option>
                <option value="new">New</option>
                <option value="top">Top</option>
                <option value="comments">Comments</option>
              </select>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                style={{
                  flex: '0 0 120px',
                  padding: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '4px',
                  background: theme.colors.background2,
                  color: theme.colors.foreground,
                }}
              >
                <option value="all">All time</option>
                <option value="year">Past year</option>
                <option value="month">Past month</option>
                <option value="week">Past week</option>
                <option value="day">Past day</option>
              </select>
              <button type="submit">Search</button>
            </form>
            {loading ? (
              <div style={{ color: theme.colors.foreground }}>Searching...</div>
            ) : (
              <div>
                {results.length === 0 ? (
                  <div style={{ color: theme.colors.foreground }}>No results found.</div>
                ) : (
                  results.map((item, index) => (
                    <Post
                      key={item.data.id}
                      post={item.data}
                      index={index}
                      isSelected={index === selectedIndex}
                      onSelect={() => selectPost(item.data)}
                      ref={el => postRefs.current[index] = el}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        {/* Details pane */}
        {expandedPost && (
          <div style={{ flex: '1', borderLeft: `1px solid ${theme.colors.border}`, display: 'block' }}>
            <PostDetails
              post={expandedPost}
              loading={detailsLoading}
              onClose={() => setExpandedPost(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;